import { useMemo } from "react";
import { SectionCard } from "../../ui/section-card";
import { SectionForm } from "../../ui/section-form";
import { describeOutcome } from "../commands";
import { type CommonKey, commonValuesFromItem, commonValuesToPatch } from "../form-mappers";
import { CommonFields, commonFieldLabels, commonFieldOrder } from "./CommonFields";
import { errorFieldsOf, type ItemSectionContext, useSectionState } from "./section-support";

const ID_PREFIX = "details";

export function CommonFieldsSection({ ctx }: Readonly<{ ctx: ItemSectionContext }>) {
  const { detail, readOnly, command } = ctx;
  const server = useMemo(() => commonValuesFromItem(detail.item), [detail.item]);
  const section = useSectionState("details", server);

  async function submit() {
    const mapped = commonValuesToPatch(section.values, detail.item);
    if (!mapped.ok) {
      section.reject(mapped.errors);
      return;
    }
    section.clearErrors();
    const outcome = await command.run("updateDetails", {
      expectedVersion: detail.item.version,
      patch: mapped.value,
    });
    section.setMessage(describeOutcome(outcome, "Details saved."));
    if (outcome.ok) section.reset(commonValuesFromItem(outcome.detail.item));
  }

  const errorFields = errorFieldsOf(
    section.errors,
    commonFieldOrder.map((key) => ({
      key,
      id: `${ID_PREFIX}-${key}`,
      label: commonFieldLabels[key],
    })),
  );

  return (
    <SectionCard
      id="details"
      title="Details"
      description="The text and place customers see, and how the item is fulfilled."
    >
      <SectionForm
        label="Item details"
        dirty={section.dirty}
        pending={command.pending}
        readOnly={readOnly}
        message={section.message}
        errorFields={errorFields}
        errorRevision={section.revision}
        onSubmit={submit}
        onDiscard={section.discard}
      >
        <CommonFields
          idPrefix={ID_PREFIX}
          values={section.values}
          errors={section.errors}
          disabled={readOnly || command.pending}
          slugLocked={detail.item.firstPublishedAt !== null}
          fulfilmentLocked={detail.item.priceAuthoritative}
          onChange={(key: CommonKey, value) =>
            section.setValues((current) => ({ ...current, [key]: value }))
          }
        />
      </SectionForm>
    </SectionCard>
  );
}
